/**
 * hq/shared/board.ts — pure Work Board rules (build-spec/BOARD-SPEC.md).
 * No I/O, no Workers types: shared by the Worker routes, the runner, and the
 * unit tests. The transition table is the contract:
 *
 *   backlog        -> in_progress     : the owning agent
 *   in_progress    -> qa              : the owning agent
 *   qa             -> human_review    : Quinn only (pass)
 *   qa             -> in_progress     : Quinn only (bounce with notes)
 *   human_review   -> scheduled_live  : founders (Approve) / the approval hook
 *   scheduled_live -> done            : the runner, with receipts
 *
 * Founders may move anything anywhere — a forward move that jumps over the
 * Quinn QA or Human review gate is allowed but logged as an override.
 */
import { CARD_COLUMNS, type CardColumnKey } from './types.js';

export const COLUMN_KEYS: readonly CardColumnKey[] = CARD_COLUMNS.map((c) => c.key);

export function isColumnKey(k: unknown): k is CardColumnKey {
  return typeof k === 'string' && (COLUMN_KEYS as readonly string[]).includes(k);
}

export function columnLabel(key: CardColumnKey): string {
  return CARD_COLUMNS.find((c) => c.key === key)?.label ?? key;
}

export function columnIndex(key: CardColumnKey): number {
  return COLUMN_KEYS.indexOf(key);
}

/**
 * May this runner-side actor move a card from -> to?
 * `actor` is the agent making the move ('system' for the runner's own hooks,
 * e.g. approval executed / experiment concluded).
 */
export function runnerMoveAllowed(
  actor: string,
  owner: string,
  from: CardColumnKey,
  to: CardColumnKey,
): boolean {
  if (from === to) return false;
  // Quinn is the only way OUT of the QA gate: pass forward or bounce back.
  if (from === 'qa') return actor === 'quinn' && (to === 'human_review' || to === 'in_progress');
  // The owning agent picks up backlog work and submits genuinely-built drafts.
  if (actor === owner) {
    if (from === 'backlog' && to === 'in_progress') return true;
    if (from === 'in_progress' && to === 'qa') return true;
  }
  // The runner's system hooks move approved work through execution.
  if (actor === 'system' || actor === 'runner') {
    if (from === 'human_review' && to === 'scheduled_live') return true;
    if (from === 'scheduled_live' && to === 'done') return true;
  }
  return false;
}

/**
 * Does a founder move from -> to skip a gate? (Allowed, but logged as an
 * override.) A gate is skipped when the move jumps forward OVER the Quinn QA
 * or Human review column without stopping in it.
 */
export function founderMoveSkipsGate(from: CardColumnKey, to: CardColumnKey): boolean {
  const a = columnIndex(from);
  const b = columnIndex(to);
  if (b <= a) return false; // backward moves never skip a gate
  for (const gate of ['qa', 'human_review'] as const) {
    const g = columnIndex(gate);
    if (a < g && b > g) return true;
  }
  return false;
}

/** Midpoint sort_order for a drop between two neighbours (either side open). */
export function midpoint(before: number | null, after: number | null): number {
  if (before == null && after == null) return 1;
  if (before == null) return (after as number) - 1;
  if (after == null) return before + 1;
  return (before + after) / 2;
}
